import { Fragment, useEffect, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { setAlert } from '../../../actions/alert';
import { crearPaquete } from '../../../actions/configuracion';
import { formatNumber } from '../../../utils/generalFunctions';

import TablaProductosPaquete from './TablaProductosPaquete';

const NuevoPaquete = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [codigo, setCodigo] = useState('');
    const [precio, setPrecio] = useState(0);
    const [iva, setIva] = useState(false);
    const [items, setItems] = useState([]);
    const [guardando, setGuardando] = useState(false);

    const precioSugerido = items.reduce(
        (acc, it) => acc + (Number(it.precio || 0) * Number(it.cantidad || 0)),
        0
    );

    const precioCompraTotal = items.reduce(
        (acc, it) => acc + (Number(it.precioCompra || 0) * Number(it.cantidad || 0)),
        0
    );

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Paquetes', path: '/configuracion/paquetes' },
            { activo: true, nombre: 'Nuevo paquete', path: '/configuracion/paquetes/nuevo' }
        ]));
    }, [dispatch]);

    const handleGuardar = async () => {
        if (!nombre.trim()) {
            dispatch(setAlert('El nombre es requerido', 'danger'));
            return;
        }

        if (!codigo.trim()) {
            dispatch(setAlert('El código es requerido', 'danger'));
            return;
        }

        if (!Number(precio) || Number(precio) <= 0) {
            dispatch(setAlert('El precio del paquete es requerido', 'danger'));
            return;
        }

        if (items.length === 0) {
            dispatch(setAlert('Agrega al menos un producto al paquete', 'danger'));
            return;
        }

        const invalido = items.find((it) => !it.productoId || !Number(it.cantidad) || Number(it.cantidad) <= 0);
        if (invalido) {
            dispatch(setAlert('Revisa que cada producto tenga una cantidad válida', 'danger'));
            return;
        }

        const productos = items.map((it) => ({
            producto: it.productoId,
            unidad: it.unidadId,
            cantidad: Number(it.cantidad || 0),
            precio: Number(it.precio || 0),
            precio_compra: Number(it.precioCompra || 0),
            conversion: Number(it.conversion || 1)
        }));

        setGuardando(true);

        const paquete = await dispatch(crearPaquete({
            nombre: nombre.trim(),
            descripcion: descripcion.trim() || null,
            codigo: codigo.trim(),
            precio: Number(precio || 0),
            precio_compra: Number(precioCompraTotal || 0),
            iva,
            productos
        }));

        setGuardando(false);

        if (!paquete?._id) return;

        navigate('/configuracion/paquetes');
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Nuevo paquete</h3>

                    <Card className="mb-3">
                        <Card.Body>
                            <div className="row g-3">
                                <div className="col-md-2">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Código</div>
                                    <Form.Control
                                        type="text"
                                        value={codigo}
                                        onChange={(e) => setCodigo(e.target.value)}
                                        placeholder="Código del paquete"
                                        disabled={guardando}
                                    />
                                </div>

                                <div className="col-md-4">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Nombre</div>
                                    <Form.Control
                                        type="text"
                                        value={nombre}
                                        onChange={(e) => setNombre(e.target.value)}
                                        placeholder="Nombre del paquete"
                                        disabled={guardando}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio</div>
                                    <CurrencyInput
                                        value={precio}
                                        decimalsLimit={2}
                                        allowNegativeValue={false}
                                        className="form-control"
                                        prefix="$ "
                                        groupSeparator=","
                                        decimalSeparator="."
                                        disabled={guardando}
                                        onValueChange={(value) => setPrecio(value === undefined || value === null || value === '' ? 0 : Number(value))}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio sugerido</div>
                                    <Form.Control
                                        type="text"
                                        value={formatNumber(precioSugerido, 2, '$ ', '')}
                                        disabled
                                        title="Calculado a partir de los productos, cantidades, precios y unidades del paquete"
                                    />
                                </div>

                                <div className="col-md-2">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Precio compra total</div>
                                    <Form.Control
                                        type="text"
                                        value={formatNumber(precioCompraTotal, 2, '$ ', '')}
                                        disabled
                                        title="Calculado a partir de los precios de compra, cantidades y unidades de los productos del paquete"
                                    />
                                </div>

                                <div className="col-md-2 d-flex align-items-end">
                                    <Form.Check
                                        type="checkbox"
                                        label="IVA"
                                        checked={iva}
                                        disabled={guardando}
                                        onChange={() => setIva(!iva)}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>Descripción (opcional)</div>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        placeholder="Descripción del paquete"
                                        disabled={guardando}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Body>
                            <div className="fw-semibold mb-2">Productos del paquete</div>
                            <TablaProductosPaquete items={items} setItems={setItems} disabled={guardando} />
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate('/configuracion/paquetes')}
                            disabled={guardando}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleGuardar}
                            disabled={guardando}
                        >
                            {guardando ? 'Guardando...' : 'Guardar paquete'}
                        </button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default NuevoPaquete;
